import { FileItem } from "@/components/elements/FileItem";
import { Modal } from "@/components/elements/Modal";
import { Notification } from "@/components/elements/Notification";
import { INFORMATION_TYPE } from "@/constants/enum";
import { INVALID_NOT_INPUT_REQUIRE_FIELD } from "@/constants/invalids";
import { TInformationForm } from "@/features/information/types/informationForm";
import { useToast } from "@/hooks/useToast";
import { background } from "@/theme/colors";
import { TExtendFile } from "@/types/file";
import { Box, Stack, Typography } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { DATE_FORMAT } from "@/constants/datetime";
import dayjs from "dayjs";
import { styled } from "@mui/system";
import { useEnum } from "@/api/services/global/enum";

type TPreviewInformationModalProps = {
  open: boolean;
  onClose: () => void;
  file?: TExtendFile | File | null;
};

export const PreviewInformationModal: FC<TPreviewInformationModalProps> = ({ open, onClose, file }) => {
  const toast = useToast();
  const { getValues } = useFormContext<Partial<TInformationForm>>();
  const value = getValues();
  const { data: informationTypes } = useEnum("EInformationType");

  const label = () => {
    const found = informationTypes.find((opt) => opt.value === value.type);
    return found ? found.label : "";
  };

  useEffect(() => {
    if (!value.type) {
      toast.error(INVALID_NOT_INPUT_REQUIRE_FIELD);
      onClose();
    }
  }, []);

  const renderContent = useMemo(() => {
    return (
      <ContentImportantStyle>
        <Stack gap={1}>
          <Typography variant="sub16Semi">{value.title}</Typography>
          {value.content && (
            <Box
              sx={{
                "& p": { minHeight: "24px" },
              }}
              dangerouslySetInnerHTML={{ __html: value.content }}
            />
          )}
          {file && <FileItem file={file} downloadable width={350} enableFileSize />}
          {value.filePath && !file && <FileItem downloadable filePath={value.filePath} width={350} enableFileSize />}
        </Stack>
      </ContentImportantStyle>
    );
  }, [value, file]);

  const renderNormalContent = useMemo(() => {
    return (
      <>
        <Typography variant="head24Bold">お知らせ</Typography>
        <Box pt={3}>
          <Notification tag={label()} title={(value.startDate ? value.startDate : dayjs()).format(DATE_FORMAT)}>
            <Stack gap={1}>
              <Typography variant="sub16Semi">{value.title}</Typography>
              {value.content && (
                <Box
                  sx={{
                    "& p": { minHeight: "24px" },
                  }}
                  dangerouslySetInnerHTML={{ __html: value.content }}
                />
              )}
              {file && <FileItem file={file} downloadable width={350} enableFileSize />}
              {value.filePath && !file && <FileItem downloadable filePath={value.filePath} width={350} enableFileSize />}
            </Stack>
          </Notification>
        </Box>
      </>
    );
  }, [value]);

  if (!value.type) return null;

  return (
    <Modal isOpen={open} onClose={onClose} title={<Typography variant="head20Bold">プレビュー</Typography>} enableFooter={false} width={1262}>
      <Box p={3} pb={3} bgcolor={background.primary} mt={3}>
        {value.type === INFORMATION_TYPE.IMPORTANT && renderContent}
        {value.type !== INFORMATION_TYPE.IMPORTANT && renderNormalContent}
      </Box>
    </Modal>
  );
};

const ContentImportantStyle = styled(Stack)`
  padding: 16px;
  border: 1px solid #ec7126;
  border-radius: 8px;
  background: #fff3e0;
`;
